import { RESOURCE_COPY } from '../content/resource-copy'
import {
  RESOURCE_TYPE_CONFIG,
  getPublishedResources,
  getResourcePath,
  getResourcesIndexPath,
} from '../content/resources'
import type { Resource, ResourceLanguage } from '../content/resources'
import type { LinkTag, RouteHead, ScriptTag } from '@/shared/seo/seo-config'
import {
  OG_IMAGE,
  SITE_NAME,
  SITE_URL,
  buildSeoHead,
} from '@/shared/seo/seo-config'

export const RESOURCES_INDEX_PATH = '/resources'
export const RESOURCES_INDEX_TITLE =
  'Laplace x GTM Blog — Guides to Finding Customers'
export const RESOURCES_INDEX_DESCRIPTION =
  'Guides to choosing who to sell to first and to sending email to new business contacts safely, one mailbox at a time.'

const RESOURCES_INDEX_SEO: Record<
  ResourceLanguage,
  { title: string; description: string }
> = {
  en: {
    title: RESOURCES_INDEX_TITLE,
    description: RESOURCES_INDEX_DESCRIPTION,
  },
  ru: {
    title: 'Материалы об управляемых AI-агентах — Laplace',
    description:
      'Практические гайды, сценарии и исследования о поиске возможностей для AI, создании управляемых агентов и оценке их влияния.',
  },
}

function absoluteAssetUrl(path: string | undefined): string {
  if (!path) return OG_IMAGE
  if (/^https?:\/\//.test(path)) return path
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

function jsonLd(value: object): ScriptTag {
  return { type: 'application/ld+json', children: JSON.stringify(value) }
}

function resourcesAlternateLinks() {
  return [
    {
      rel: 'alternate',
      hrefLang: 'en',
      href: `${SITE_URL}${getResourcesIndexPath('en')}`,
    },
    {
      rel: 'alternate',
      hrefLang: 'ru',
      href: `${SITE_URL}${getResourcesIndexPath('ru')}`,
    },
    {
      rel: 'alternate',
      hrefLang: 'x-default',
      href: `${SITE_URL}${getResourcesIndexPath('ru')}`,
    },
  ]
}

function resourceAlternateLinks(resource: Resource) {
  const links: Array<LinkTag> = (['en', 'ru'] as const).flatMap((language) => {
    const translation = getPublishedResources(language).find(
      (candidate) =>
        candidate.type === resource.type && candidate.slug === resource.slug,
    )
    return translation
      ? [
          {
            rel: 'alternate',
            hrefLang: language,
            href: `${SITE_URL}${getResourcePath(translation)}`,
          },
        ]
      : []
  })
  const russian = links.find((link) => link.hrefLang === 'ru')
  if (russian) {
    links.push({ ...russian, hrefLang: 'x-default' })
  }
  return links
}

export function buildResourcesIndexHead(
  language: ResourceLanguage = 'en',
): RouteHead {
  const seo = RESOURCES_INDEX_SEO[language]
  const canonical = `${SITE_URL}${getResourcesIndexPath(language)}`
  return buildSeoHead({
    title: seo.title,
    description: seo.description,
    canonical,
    links: resourcesAlternateLinks(),
    scripts: [
      jsonLd({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: seo.title,
        description: seo.description,
        url: canonical,
        inLanguage: language,
        isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
      }),
    ],
  })
}

export function buildResourceHead(resource: Resource): RouteHead {
  const path = getResourcePath(resource)
  const canonical = `${SITE_URL}${path}`
  const title = resource.seoTitle ?? `${resource.title} — Laplace Resources`
  const description = resource.seoDescription ?? resource.description
  const image = absoluteAssetUrl(resource.coverImage)
  const copy = RESOURCE_COPY[resource.language]
  const typeConfig = RESOURCE_TYPE_CONFIG[resource.language][resource.type]
  const resourcesIndexUrl = `${SITE_URL}${getResourcesIndexPath(resource.language)}`
  const categoryUrl = `${resourcesIndexUrl}#${typeConfig.segment}`

  const article = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: resource.title,
    description,
    url: canonical,
    mainEntityOfPage: canonical,
    image: [image],
    datePublished: resource.publishedAt,
    dateModified: resource.updatedAt ?? resource.publishedAt,
    author: { '@type': 'Organization', name: resource.author },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: OG_IMAGE },
    },
    inLanguage: resource.language,
    articleSection: typeConfig.label,
    ...(resource.topics?.length
      ? { keywords: resource.topics.join(', ') }
      : {}),
  }

  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: copy.resources,
        item: resourcesIndexUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: typeConfig.pluralLabel,
        item: categoryUrl,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: resource.title,
        item: canonical,
      },
    ],
  }

  const faqItems = resource.sections.flatMap((section) =>
    section.blocks.flatMap((block) =>
      block.type === 'faq' ? block.items : [],
    ),
  )

  const faqPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }

  return buildSeoHead({
    title,
    description,
    canonical,
    image,
    ogType: 'article',
    links: resourceAlternateLinks(resource),
    meta: [
      {
        property: 'article:published_time',
        content: resource.publishedAt,
      },
      {
        property: 'article:modified_time',
        content: resource.updatedAt ?? resource.publishedAt,
      },
    ],
    scripts: [
      jsonLd(article),
      jsonLd(breadcrumbs),
      ...(faqItems.length > 0 ? [jsonLd(faqPage)] : []),
    ],
  })
}
